import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Typography,
  makeStyles,
  Link,
} from '@material-ui/core';

import EditCard from 'components/EditCard';

import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import 'translations/translations';
import { useTranslation } from 'react-i18next';
import { organizationActions } from 'actions/organizationActions';
import { notificationActions } from 'actions/notificationActions';

const useStyles = makeStyles(() => ({
  label: {
    fontWeight: 'bold',
  },
  link: { fontWeight: 'normal' },
}));

const ContactInformationCard: React.FC = () => {
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();
  const classes = useStyles();

  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState({ email: '', phone: '', webSite: '' });
  const [isMounted, setIsMounted] = useState(true);

  const organization = useSelector(
    (state: ApplicationState) => state.organization?.organization,
  );

  // track unmount
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  // track organization
  useEffect(() => {
    resetInput();
  }, [organization]);

  const getClickableLink = (link: string) => {
    return link.startsWith('http://') || link.startsWith('https://')
      ? link
      : `http://${link}`;
  };

  const handleSave = () => {
    if (organization?.contactInformation) {
      setIsLoading(true);
      dispatch(
        organizationActions.updateOrganizationContactDetails(
          organization.organizationId,
          input,
          (error) => {
            if (isMounted) {
              setIsLoading(false);
              setEditMode(!!error);
            }
            if (error) {
              dispatch(notificationActions.enqueueErrorResponse(error));
            }
          },
        ),
      );
    }
  };

  const resetInput = () => {
    if (organization?.contactInformation) {
      setInput({
        email: organization.contactInformation.email ?? '',
        phone: organization.contactInformation.phone ?? '',
        webSite: organization.contactInformation.webSite ?? '',
      });
    } else {
      setInput({ email: '', phone: '', webSite: '' });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInput((input) => ({ ...input, [name]: value }));
  };

  const handleEdit = () => {
    resetInput();
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  return (
    <EditCard
      editMode={editMode}
      isLoading={isLoading}
      title={t(
        'organization_page.organization_information.labels.contact_information',
      )}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
    >
      {editMode ? (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              type="text"
              variant="outlined"
              margin="none"
              fullWidth
              label={t(
                'organization_page.organization_information.labels.email',
              )}
              name="email"
              value={input.email}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              type="text"
              variant="outlined"
              margin="none"
              fullWidth
              label={t(
                'organization_page.organization_information.labels.phone',
              )}
              name="phone"
              value={input.phone}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              type="text"
              variant="outlined"
              margin="none"
              fullWidth
              label={t(
                'organization_page.organization_information.labels.website',
              )}
              name="webSite"
              value={input.webSite}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      ) : (
        <>
          <Typography variant="body1">
            <span className={classes.label}>
              {t('organization_page.organization_information.labels.phone')}:
            </span>
            &nbsp;
            {input.phone}
          </Typography>
          <Typography variant="body1">
            <span className={classes.label}>
              {t('organization_page.organization_information.labels.email')}:
            </span>
            &nbsp;
            {input.email && (
              <Link href={`mailto:${input.email}`} className={classes.link}>
                {input.email}
              </Link>
            )}
          </Typography>
          <Typography variant="body1">
            <span className={classes.label}>
              {t('organization_page.organization_information.labels.website')}:
            </span>
            &nbsp;
            {input.webSite && (
              <Link
                href={getClickableLink(input.webSite || '')}
                target="blank"
                className={classes.link}
              >
                {input.webSite}
              </Link>
            )}
          </Typography>
        </>
      )}
    </EditCard>
  );
};

export default ContactInformationCard;
